"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import { getViewId } from "@/datadog/view-id";
import { VIEW_ID_HEADER } from "@/shared/request-ids";
import { TasksResponse } from "@/types";
import {
	POLL_REQUEST_TIMEOUT_MS,
	type WaitingPhase,
	emptyFailedPollBudget,
	emptyHealthyPollBudget,
	isBrowserReadyToPoll,
	isFailedPollBudgetExhausted,
	isHealthyPollBudgetExhausted,
	isPollNetworkFailure,
	pauseFailedPollBudget,
	pauseHealthyPollBudget,
	pollDelayForAttempt,
	recordFailedPoll,
	recordHealthyPoll,
	waitingPhaseFor,
} from "./poll-schedule";

const MOCK_POLL_HEADERS = { Authorization: "Bearer mock-token" };

const pollHeaders = (mock: boolean): HeadersInit => {
	const viewId = getViewId();
	return {
		...(mock ? MOCK_POLL_HEADERS : {}),
		...(viewId ? { [VIEW_ID_HEADER]: viewId } : {}),
	};
};

const readBrowserPollState = () => ({
	visible: document.visibilityState === "visible",
	online: navigator.onLine,
});

/**
 * Polls FLOW for the next task and navigates when one appears. No-op when
 * `flowId` is missing. The next request is scheduled only after the previous
 * one settles, so a slow backend cannot stack overlapping reads.
 */
export const useFlowTaskPolling = ({
	flowId,
	ignoreTaskId,
}: {
	flowId?: string;
	/**
	 * A task id that does not count as progress - the signing task this browser
	 * has already completed, which FLOW has yet to close.
	 */
	ignoreTaskId?: string;
}): WaitingPhase => {
	const router = useRouter();
	const [phase, setPhase] = useState<WaitingPhase>("normal");
	const { showErrors, yellowStateUrl, errorUrl, mock } = useRuntimeConfig();

	useEffect(() => {
		if (!flowId) {
			return;
		}

		let finished = false;
		let inFlight = false;
		let unavailable = false;
		let loggedNetwork = false;
		let loggedUnavailable = false;
		let attempts = 0;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let healthyBudget = emptyHealthyPollBudget();
		let failedBudget = emptyFailedPollBudget();
		const unmountController = new AbortController();
		const startedAt = Date.now();

		void sendClientLog({
			level: "info",
			event: LogEvent.PollStarted,
			message: "Waiting screen started polling for the next task",
			flowId,
		});

		const elapsedMs = () => Date.now() - startedAt;

		const updatePhase = () => {
			setPhase(
				waitingPhaseFor({
					elapsedMs: elapsedMs(),
					online: readBrowserPollState().online,
					unavailable,
				})
			);
		};

		const finish = (action: () => void) => {
			if (finished) return;
			finished = true;
			if (timer !== undefined) clearTimeout(timer);
			action();
		};

		const leaveForYellowState = () => {
			if (showErrors) {
				router.push(
					`/debug-error?${new URLSearchParams({
						reason: "Waiting page timed out",
						flowId,
					}).toString()}`
				);
				return;
			}
			if (yellowStateUrl) {
				window.location.replace(yellowStateUrl);
			}
		};

		const leaveForErrorPage = () => {
			if (showErrors) {
				router.push(
					`/debug-error?${new URLSearchParams({
						reason: "Waiting page: FLOW unavailable",
						flowId,
					}).toString()}`
				);
				return;
			}
			if (errorUrl) {
				window.location.replace(errorUrl);
			}
		};

		const timeoutOnHealthyBudget = () => {
			finish(() => {
				void sendClientLog({
					level: "warn",
					event: LogEvent.PollTimeout,
					outcome: "failure",
					message: "Waiting screen timed out without receiving a task",
					flowId,
					durationMs: elapsedMs(),
					context: { attempts, healthyEmptyMs: healthyBudget.accumulatedMs },
				});
				leaveForYellowState();
			});
		};

		const timeoutOnFailedBudget = () => {
			finish(() => {
				void sendClientLog({
					level: "error",
					event: LogEvent.PollUnavailableTimeout,
					outcome: "failure",
					message: "Waiting screen gave up after the tasks poll kept failing",
					flowId,
					durationMs: elapsedMs(),
					context: { attempts, failedMs: failedBudget.accumulatedMs },
				});
				leaveForErrorPage();
			});
		};

		const recordUnavailablePoll = (statusCode: number) => {
			healthyBudget = pauseHealthyPollBudget(healthyBudget);
			failedBudget = recordFailedPoll(failedBudget, Date.now());
			if (!unavailable) {
				unavailable = true;
				updatePhase();
			}
			if (loggedUnavailable) return;
			loggedUnavailable = true;
			void sendClientLog({
				level: "warn",
				event: LogEvent.PollUnavailable,
				outcome: "failure",
				message: "Waiting screen poll got an error from the tasks API",
				flowId,
				statusCode,
				durationMs: elapsedMs(),
				context: { attempts },
			});
		};

		const browserIsReady = () => isBrowserReadyToPoll(readBrowserPollState());

		const scheduleNext = () => {
			if (finished || inFlight || !browserIsReady()) return;
			timer = setTimeout(() => {
				timer = undefined;
				void tick();
			}, pollDelayForAttempt(attempts));
		};

		const tick = async () => {
			if (finished || inFlight || !browserIsReady()) return;
			inFlight = true;
			attempts += 1;
			updatePhase();

			const requestController = new AbortController();
			const abortRequest = () => requestController.abort();
			unmountController.signal.addEventListener("abort", abortRequest);
			const requestTimer = setTimeout(abortRequest, POLL_REQUEST_TIMEOUT_MS);

			try {
				const response = await fetch(`/api/flow/${flowId}/tasks`, {
					signal: requestController.signal,
					headers: pollHeaders(mock),
				});

				if (finished) return;

				// The resolver sends a customer without a session to login; waiting
				// out the unavailable budget would hide that for five minutes.
				if (response.status === 401) {
					finish(() => {
						void sendClientLog({
							level: "warn",
							event: LogEvent.PollSessionLost,
							outcome: "failure",
							message:
								"Waiting screen lost the session; leaving for the resolver",
							flowId,
							durationMs: elapsedMs(),
							context: { attempts },
						});
						router.replace(`/${flowId}`);
					});
					return;
				}

				if (!response.ok) {
					recordUnavailablePoll(response.status);
					if (isFailedPollBudgetExhausted(failedBudget)) {
						timeoutOnFailedBudget();
						return;
					}
				} else {
					failedBudget = emptyFailedPollBudget();
					if (unavailable) {
						unavailable = false;
						updatePhase();
					}

					const data: TasksResponse = await response.json();
					const nextTask = data.tasks?.[0];
					if (nextTask && nextTask.taskId !== ignoreTaskId) {
						finish(() => {
							void sendClientLog({
								level: "info",
								event: LogEvent.PollResolved,
								outcome: "success",
								message: "Waiting screen resolved a task",
								flowId,
								task: nextTask.taskType,
								durationMs: elapsedMs(),
								context: { attempts },
							});
							router.replace(`/${flowId}`);
						});
						return;
					}

					if (browserIsReady()) {
						healthyBudget = recordHealthyPoll(healthyBudget, Date.now());
						if (isHealthyPollBudgetExhausted(healthyBudget)) {
							timeoutOnHealthyBudget();
							return;
						}
					} else {
						healthyBudget = pauseHealthyPollBudget(healthyBudget);
					}
				}
			} catch (error) {
				if (unmountController.signal.aborted) return;
				const lastError =
					error instanceof Error ? error : new Error(String(error));
				healthyBudget = pauseHealthyPollBudget(healthyBudget);
				failedBudget = pauseFailedPollBudget(failedBudget);
				if (isPollNetworkFailure(lastError) && !loggedNetwork) {
					loggedNetwork = true;
					void sendClientLog({
						level: "warn",
						event: LogEvent.PollNetwork,
						outcome: "failure",
						message: "Waiting screen poll failed to reach the tasks API",
						flowId,
						error: lastError,
						durationMs: elapsedMs(),
						context: { attempts },
					});
				}
			} finally {
				clearTimeout(requestTimer);
				unmountController.signal.removeEventListener("abort", abortRequest);
				inFlight = false;
			}

			if (finished) return;
			scheduleNext();
		};

		const onBrowserPollStateChange = () => {
			if (finished) return;
			updatePhase();
			if (!browserIsReady()) {
				healthyBudget = pauseHealthyPollBudget(healthyBudget);
				failedBudget = pauseFailedPollBudget(failedBudget);
				if (timer !== undefined) {
					clearTimeout(timer);
					timer = undefined;
				}
				return;
			}
			if (inFlight || timer !== undefined) return;
			void tick();
		};

		void tick();
		const phaseTicker = setInterval(updatePhase, 1_000);
		document.addEventListener("visibilitychange", onBrowserPollStateChange);
		window.addEventListener("online", onBrowserPollStateChange);
		window.addEventListener("offline", onBrowserPollStateChange);
		window.addEventListener("pagehide", onBrowserPollStateChange);
		window.addEventListener("pageshow", onBrowserPollStateChange);

		return () => {
			finished = true;
			// Unmount abort is ignored in the poller; Datadog should not see it as a network error.
			unmountController.abort();
			if (timer !== undefined) clearTimeout(timer);
			clearInterval(phaseTicker);
			document.removeEventListener(
				"visibilitychange",
				onBrowserPollStateChange
			);
			window.removeEventListener("online", onBrowserPollStateChange);
			window.removeEventListener("offline", onBrowserPollStateChange);
			window.removeEventListener("pagehide", onBrowserPollStateChange);
			window.removeEventListener("pageshow", onBrowserPollStateChange);
		};
	}, [
		flowId,
		ignoreTaskId,
		router,
		showErrors,
		yellowStateUrl,
		errorUrl,
		mock,
	]);

	return phase;
};
